import { CloseIcon } from '@chakra-ui/icons'
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Tag,
  TagLabel,
  TagLeftIcon,
  TagRightIcon,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import type { ReactElement } from 'react'
import { useContext } from 'react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsTagsFill } from 'react-icons/bs'
import useFetch, { CachePolicies } from 'use-http'
import AWSIcon from '../../../../graphics/AWSIcon'
import GoogleIcon from '../../../../graphics/GoogleIcon'
import { colors } from '../../../../ui-kit'
import { checkOperationPermission, PermissionsEnum } from '../../../../utils'
import {
  allowExtensionOperations,
  canAddTags,
  encodeStringForUrl,
} from '../../../../utils/mediaLibrary'
import { ACLPermissionEnum } from '../../../constants'
import type { FileDetailsResponse } from '../../../interfaces/FileDetailsResponse'
import { UserContext } from '../../../providers'
import PermissionGuard from '../../PermissionGuard'
import AddTagsModal from './AddTagsModal'
import AutoTagsModal from './AutoTagsModal'
import { useHistory } from 'react-router-dom'
import { SearchQueryJson } from '../../../interfaces'
import { stringify } from 'query-string'

const Tags = ({
  fileDetails,
  isTagsModalOpen,
  toggleTagsModal,
  getFileDetails,
}: {
  fileDetails: FileDetailsResponse
  isTagsModalOpen: boolean
  toggleTagsModal(isTagsModalOpen: boolean): void
  getFileDetails(): Promise<FileDetailsResponse>
}): ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const [isAutoTagsModalOpen, toggleAutoTagsModal] = useState(false)
  const routerHistory = useHistory()

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const deleteAITag = async (
    fileId: string,
    tagToRemove: string,
    onClose: () => void,
  ): Promise<void> => {
    const response = await request.post('/api/v2/files/removeAITags', {
      fileIds: [fileId],
      AITags: [tagToRemove],
    })

    onClose()

    if (response.successfullyUpdatedFileIds?.length > 0) {
      await getFileDetails()
    }
  }

  const removeTags = async ({
    fileId,
    tagToRemove,
    onClose,
  }: {
    fileId: string
    tagToRemove: string
    onClose(): void
  }): Promise<void> => {
    const response = await request.post('/api/v2/files/removeTags', {
      fileIds: [fileId],
      tags: [tagToRemove],
    })

    onClose()

    if (response.successfullyUpdatedFileIds?.length > 0) {
      await getFileDetails()
    }
  }

  return (
    <PermissionGuard
      permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW}
    >
      {(hasPermission: boolean) =>
        hasPermission ? (
          <React.Fragment>
            <AccordionItem borderTopWidth={0} borderBottomWidth={1}>
              <AccordionButton py={6}>
                <Flex
                  flex="1"
                  textAlign="left"
                  lineHeight="1.5em"
                  fontWeight="semibold"
                  alignItems="center"
                  style={{ gap: '1em' }}
                >
                  <BsTagsFill fontSize="1.5em" />
                  {t('tags', 'Tags')}
                </Flex>
                <AccordionIcon color={colors.appSlate} display="block" />
              </AccordionButton>

              <AccordionPanel>
                <VStack spacing="4" width="full" justifyContent="flex-start">
                  <PermissionGuard
                    permission={
                      PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
                    }
                  >
                    {(hasTagsWritePermission: boolean) =>
                      hasTagsWritePermission ? (
                        <Flex width="full">
                          {canAddTags(fileDetails) &&
                          checkOperationPermission({
                            user,
                            aclPermission: [
                              ACLPermissionEnum.CONTRIBUTE,
                              ACLPermissionEnum.MANAGE,
                            ],
                            selectedFiles: [fileDetails],
                            selectedFolders: [],
                            selectedMediaCollections: [],
                          }) ? (
                            <Button
                              onClick={() => {
                                toggleTagsModal(true)
                              }}
                            >
                              {t('edit_tags', 'Edit tags')}
                            </Button>
                          ) : (
                            <></>
                          )}
                          {allowExtensionOperations(fileDetails) &&
                          checkOperationPermission({
                            user,
                            aclPermission: [
                              ACLPermissionEnum.CONTRIBUTE,
                              ACLPermissionEnum.MANAGE,
                            ],
                            selectedFiles: [fileDetails],
                            selectedFolders: [],
                            selectedMediaCollections: [],
                          }) ? (
                            <Button
                              marginLeft={5}
                              onClick={() => {
                                toggleAutoTagsModal(true)
                              }}
                            >
                              {t('add_auto_tags', 'Add auto tags')}
                            </Button>
                          ) : (
                            <></>
                          )}
                        </Flex>
                      ) : (
                        <></>
                      )
                    }
                  </PermissionGuard>
                  <Flex width="full" flexWrap="wrap">
                    {(fileDetails.tags ?? []).map(item => (
                      <Tooltip label="Search for tag"  placement="bottom">
                        <Tag
                          key={item}
                          variant="outline"
                          boxShadow="inset 0 0 0px 1px #E6E7EA"
                          fontWeight="500"
                          height="32px"
                          display="inline-flex"
                          justifyContent="flex-start"
                          alignItems="center"
                          gap="5px"
                          size="lg"
                          marginRight="5px"
                          marginBottom="10px"
                          rounded="50"
                          bg="#FFFFFF"
                          color="#3B485E"
                          _hover={{ 
                            bg: "#E6EFFF",
                            textColor: "#0450D5",
                            cursor: 'pointer',
                            boxShadow: "inset 0 0 0px 1px #E6EFFF"
                          }}
                          onClick={() => {
                            const searchQuery = `(tags IN ["${item}"])`,
                              searchQueryJson = encodeStringForUrl(JSON.stringify({
                                "boolean": "and",
                                "filterGroups": [
                                  {
                                    "boolean": "and",
                                    "expressions": [
                                      {
                                        "fieldName": "tags",
                                        "operator": "IS_ONE_OF",
                                        "value": [
                                          item
                                        ]
                                      }
                                    ]
                                  }
                                ]
                              } as SearchQueryJson))
                            routerHistory?.push({
                              pathname: '/media-library',
                              search: stringify({
                                searchQuery,
                                searchQueryJson
                              }, { skipNull: true })
                            })
                          }}
                        >
                          <TagLabel
                            lineHeight="1.3125"
                            wordBreak="break-word"
                          >
                            {item}
                          </TagLabel>
                        <PermissionGuard
                          permission={
                            PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
                          }
                        >
                          {(hasTagsDeletePermission: boolean) =>
                            hasTagsDeletePermission &&
                            canAddTags(fileDetails) &&
                            checkOperationPermission({
                              user,
                              aclPermission: [
                                ACLPermissionEnum.CONTRIBUTE,
                                ACLPermissionEnum.MANAGE,
                              ],
                              selectedFiles: [fileDetails],
                              selectedFolders: [],
                              selectedMediaCollections: [],
                            }) ? (
                              <Popover>
                                {({ onClose }) => (
                                  <>
                                    <PopoverTrigger>
                                      <Box
                                        position="relative"
                                        width="24px"
                                        height="24px"
                                        marginRight="-5px"
                                        borderRadius="32"
                                        cursor="pointer"
                                        overflow="hidden"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                        }}
                                        textColor={"#4A5568"}
                                        _hover={{ bg: "#cddcf7", textColor: "#1A202C" }}
                                      >
                                        <TagRightIcon as={CloseIcon} width="8.95px" height="8.95px" marginLeft="7.52px" marginTop="7.53px" position="absolute" />
                                      </Box>
                                    </PopoverTrigger>
                                    <PopoverContent onClick={(event) => {
                                      event.stopPropagation();
                                    }}>
                                      <PopoverArrow />
                                      <PopoverCloseButton color="appSlate" />
                                      <PopoverHeader color="appSlate">
                                        {t('delete_tag', 'Delete AI tag')}
                                      </PopoverHeader>
                                      <PopoverBody color="appSlate">
                                        <VStack
                                          spacing="4"
                                          justifyContent="flex-start"
                                          width="full"
                                          alignItems="flex-start"
                                        >
                                          <Text>
                                            {t(
                                              'delete_confirmation_message',
                                              'Are you sure you want to delete?',
                                            )}
                                          </Text>
                                          <Button
                                            colorScheme="appBrick"
                                            variant="solid"
                                            onClick={() => {
                                              void removeTags({
                                                fileId: fileDetails.fileId,
                                                tagToRemove: item,
                                                onClose,
                                              })
                                            }}
                                          >
                                            {t('confirm', 'Confirm')}
                                          </Button>
                                        </VStack>
                                      </PopoverBody>
                                    </PopoverContent>
                                  </>
                                )}
                              </Popover>
                            ) : (
                              <></>
                            )
                          }
                        </PermissionGuard>
                      </Tag>
                      </Tooltip>
                    ))}
                    {(fileDetails.AITags ?? []).map(item => (
                      <Tooltip label="Search for tag"  placement="bottom">
                      <Tag
                        key={item.name}
                        variant="outline"
                        boxShadow="inset 0 0 0px 1px #E6E7EA"
                        fontWeight="500"
                          height="32px"
                          display="inline-flex"
                          justifyContent="flex-start"
                          alignItems="center"
                          gap="5px"
                          size="lg"
                          marginRight="5px"
                          marginBottom="10px"
                          rounded="50"
                          bg="#FFFFFF"
                          color="#3B485E"
                          _hover={{ 
                            bg: "#E6EFFF",
                            textColor: "#0450D5",
                            cursor: 'pointer',
                            boxShadow: "inset 0 0 0px 1px #E6EFFF"
                          }}
                        onClick={() => {
                          const searchQuery = `(tags IN ["${item.name}"])`,
                            searchQueryJson = encodeStringForUrl(JSON.stringify({
                              "boolean": "and",
                              "filterGroups": [
                                {
                                  "boolean": "and",
                                  "expressions": [
                                    {
                                      "fieldName": "tags",
                                      "operator": "IS_ONE_OF",
                                      "value": [
                                        item.name
                                      ]
                                    }
                                  ]
                                }
                              ]
                            } as SearchQueryJson));
                          routerHistory?.push({
                            pathname: '/media-library',
                            search: stringify({
                              searchQuery,
                              searchQueryJson
                            }, { skipNull: true })
                          })
                        }}
                      >
                        <TagLeftIcon
                          as={
                            item.source === 'google-auto-tagging'
                              ? GoogleIcon
                              : AWSIcon
                          }
                          background="white"
                          borderRadius="full"
                          padding="2px"
                          boxSize="1.25rem"
                          marginRight="0px"
                        />
                          <TagLabel
                            lineHeight="1.3125"
                            wordBreak="break-word">
                            {item.name}
                          </TagLabel>
                        <PermissionGuard
                          permission={
                            PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
                          }
                        >
                          {(hasTagsDeletePermission: boolean) =>
                            hasTagsDeletePermission &&
                            canAddTags(fileDetails) &&
                            checkOperationPermission({
                              user,
                              aclPermission: [
                                ACLPermissionEnum.CONTRIBUTE,
                                ACLPermissionEnum.MANAGE,
                              ],
                              selectedFiles: [fileDetails],
                              selectedFolders: [],
                              selectedMediaCollections: [],
                            }) ? (
                              <Popover>
                                {({ onClose }) => (
                                  <>
                                    <PopoverTrigger>
                                    <Box
                                        position="relative"
                                        width="24px"
                                        height="24px"
                                        marginRight="-5px"
                                        borderRadius="32"
                                        cursor="pointer"
                                        overflow="hidden"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                        }}
                                        textColor={"#4A5568"}
                                        _hover={{ bg: "#cddcf7", textColor: "#1A202C" }}
                                      >
                                        <TagRightIcon as={CloseIcon} width="8.95px" height="8.95px" marginLeft="7.52px" marginTop="7.53px" position="absolute" />
                                      </Box>
                                    </PopoverTrigger>
                                    <PopoverContent onClick={(event) => {
                                      event.stopPropagation();
                                    }}>
                                      <PopoverArrow />
                                      <PopoverCloseButton color="appSlate" />
                                      <PopoverHeader color="appSlate">
                                        {t('delete_ai_tag', 'Delete AI tag')}
                                      </PopoverHeader>
                                      <PopoverBody color="appSlate">
                                        <VStack
                                          spacing="4"
                                          justifyContent="flex-start"
                                          width="full"
                                          alignItems="flex-start"
                                        >
                                          <Text>
                                            {t(
                                              'delete_confirmation_message',
                                              'Are you sure you want to delete?',
                                            )}
                                          </Text>
                                          <Button
                                            colorScheme="appBrick"
                                            variant="solid"
                                            onClick={() => {
                                              void deleteAITag(
                                                fileDetails.fileId,
                                                item.name,
                                                onClose,
                                              )
                                            }}
                                          >
                                            {t('confirm', 'Confirm')}
                                          </Button>
                                        </VStack>
                                      </PopoverBody>
                                    </PopoverContent>
                                  </>
                                )}
                              </Popover>
                            ) : (
                              <></>
                            )
                          }
                        </PermissionGuard>
                      </Tag>
                      </Tooltip>
                    ))}
                  </Flex>
                </VStack>
              </AccordionPanel>
            </AccordionItem>
            <AddTagsModal
              fileId={fileDetails.fileId}
              data={fileDetails.tags ?? []}
              isOpen={isTagsModalOpen}
              onClose={() => {
                toggleTagsModal(false)
              }}
              getFileDetails={() => {
                void getFileDetails()
              }}
            />
            <AutoTagsModal
              fileId={fileDetails.fileId}
              isOpen={isAutoTagsModalOpen}
              onClose={() => {
                toggleAutoTagsModal(false)
              }}
              getFileDetails={() => {
                void getFileDetails()
              }}
            />
          </React.Fragment>
        ) : (
          <></>
        )
      }
    </PermissionGuard>
  )
}

export default Tags
